import React, { useState } from 'react';
import { CircularProgress } from '@mui/material';
import './ContactForm.scss';
import { optionsForWhatsDescribesYou } from '../Common/Constant.js';
import GenericDialog from '../GenericDialog/GenericDialog.js';

const INITIAL_DATA = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    workEmail: '',
    city: '',
    companyName: '',
    description: '',
    help: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Low-friction contact form.
 *  - Required: first name, last name, work email, "which describes you", message.
 *  - Optional: phone, company name, city (so individual clinicians and patients
 *    aren't blocked by B2B-only fields).
 *  - `defaultDescription` lets the page preset the audience from ?audience=...
 *  - PRESERVES the original POST-to-/api/contact submit + GenericDialog handling.
 */
const ContactForm = ({ className = '', defaultDescription = '' }) => {
    const [dialogState, setDialogState] = useState({
        open: false,
        title: '',
        message: '',
        severity: 'success',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        ...INITIAL_DATA,
        description: defaultDescription || '',
    });
    const [errors, setErrors] = useState({});

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

    const validateField = (name, value) => {
        switch (name) {
            case 'firstName':
                return value.trim() ? '' : 'Please enter your first name.';
            case 'lastName':
                return value.trim() ? '' : 'Please enter your last name.';
            case 'workEmail':
                if (!value.trim()) return 'Please enter your email address.';
                return EMAIL_RE.test(value.trim())
                    ? ''
                    : 'Please enter a valid email address.';
            case 'description':
                return value ? '' : 'Please choose the option that describes you.';
            case 'help':
                return value.trim() ? '' : 'Please tell us how we can help.';
            default:
                return '';
        }
    };

    const validateAll = () => {
        const nextErrors = {};
        ['firstName', 'lastName', 'workEmail', 'description', 'help'].forEach((name) => {
            const message = validateField(name, formData[name]);
            if (message) nextErrors[name] = message;
        });
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => {
            if (!prev[name]) return prev;
            const next = { ...prev };
            delete next[name];
            return next;
        });
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const message = validateField(name, value);
        setErrors((prev) => {
            if (message) return { ...prev, [name]: message };
            if (!prev[name]) return prev;
            const next = { ...prev };
            delete next[name];
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateAll()) return;

        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                setDialogState({
                    open: true,
                    title: 'Message sent',
                    message:
                        'Thank you. Your message has been sent, and a member of our team will follow up shortly.',
                    severity: 'success',
                });
                setFormData({ ...INITIAL_DATA, description: defaultDescription || '' });
                setErrors({});
            } else {
                const errorMessage = await response.text();
                setDialogState({
                    open: true,
                    title: 'Something went wrong',
                    message:
                        errorMessage ||
                        'There was an issue sending your message. Please try again.',
                    severity: 'error',
                });
            }
        } catch (error) {
            setDialogState({
                open: true,
                title: 'Something went wrong',
                message: 'An error occurred. Please try again later.',
                severity: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={`contact-form ${className}`.trim()}>
            <form className="contact-form__form" onSubmit={handleSubmit} noValidate>
                <div className="contact-form__row">
                    <div className="contact-form__field">
                        <label htmlFor="cf-firstName">
                            First name <span aria-hidden="true">*</span>
                        </label>
                        <input
                            id="cf-firstName"
                            name="firstName"
                            type="text"
                            autoComplete="given-name"
                            value={formData.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            aria-required="true"
                            aria-invalid={!!errors.firstName}
                            aria-describedby={errors.firstName ? 'cf-firstName-err' : undefined}
                        />
                        {errors.firstName && (
                            <span className="contact-form__error" id="cf-firstName-err" role="alert">
                                {errors.firstName}
                            </span>
                        )}
                    </div>

                    <div className="contact-form__field">
                        <label htmlFor="cf-lastName">
                            Last name <span aria-hidden="true">*</span>
                        </label>
                        <input
                            id="cf-lastName"
                            name="lastName"
                            type="text"
                            autoComplete="family-name"
                            value={formData.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            aria-required="true"
                            aria-invalid={!!errors.lastName}
                            aria-describedby={errors.lastName ? 'cf-lastName-err' : undefined}
                        />
                        {errors.lastName && (
                            <span className="contact-form__error" id="cf-lastName-err" role="alert">
                                {errors.lastName}
                            </span>
                        )}
                    </div>
                </div>

                <div className="contact-form__row">
                    <div className="contact-form__field">
                        <label htmlFor="cf-workEmail">
                            Email <span aria-hidden="true">*</span>
                        </label>
                        <input
                            id="cf-workEmail"
                            name="workEmail"
                            type="email"
                            autoComplete="email"
                            value={formData.workEmail}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            aria-required="true"
                            aria-invalid={!!errors.workEmail}
                            aria-describedby={errors.workEmail ? 'cf-workEmail-err' : undefined}
                        />
                        {errors.workEmail && (
                            <span className="contact-form__error" id="cf-workEmail-err" role="alert">
                                {errors.workEmail}
                            </span>
                        )}
                    </div>

                    <div className="contact-form__field">
                        <label htmlFor="cf-phone">Phone (optional)</label>
                        <input
                            id="cf-phone"
                            name="phoneNumber"
                            type="tel"
                            autoComplete="tel"
                            inputMode="tel"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                <div className="contact-form__row">
                    <div className="contact-form__field">
                        <label htmlFor="cf-company">Organization (optional)</label>
                        <input
                            id="cf-company"
                            name="companyName"
                            type="text"
                            autoComplete="organization"
                            value={formData.companyName}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="contact-form__field">
                        <label htmlFor="cf-city">City (optional)</label>
                        <input
                            id="cf-city"
                            name="city"
                            type="text"
                            autoComplete="address-level2"
                            value={formData.city}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                <div className="contact-form__field">
                    <label htmlFor="cf-description">
                        Which describes you best? <span aria-hidden="true">*</span>
                    </label>
                    <select
                        id="cf-description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        aria-required="true"
                        aria-invalid={!!errors.description}
                        aria-describedby={errors.description ? 'cf-description-err' : undefined}
                    >
                        <option value="">Select one…</option>
                        {optionsForWhatsDescribesYou.map((val) => (
                            <option value={val.value} key={val.value}>
                                {val.label}
                            </option>
                        ))}
                    </select>
                    {errors.description && (
                        <span className="contact-form__error" id="cf-description-err" role="alert">
                            {errors.description}
                        </span>
                    )}
                </div>

                <div className="contact-form__field">
                    <label htmlFor="cf-help">
                        How can we help? <span aria-hidden="true">*</span>
                    </label>
                    <textarea
                        id="cf-help"
                        name="help"
                        rows={5}
                        value={formData.help}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        aria-required="true"
                        aria-invalid={!!errors.help}
                        aria-describedby={errors.help ? 'cf-help-err' : undefined}
                    />
                    {errors.help && (
                        <span className="contact-form__error" id="cf-help-err" role="alert">
                            {errors.help}
                        </span>
                    )}
                </div>

                <button
                    type="submit"
                    className="btn btn--gold contact-form__submit"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <CircularProgress size={22} color="inherit" />
                    ) : (
                        'Send message'
                    )}
                </button>
            </form>

            <GenericDialog
                open={dialogState.open}
                onClose={() => setDialogState({ ...dialogState, open: false })}
                title={dialogState.title}
                message={dialogState.message}
                severity={dialogState.severity}
            />
        </div>
    );
};

export default ContactForm;
